import { compact, omit, pick } from 'lodash'

import { getChangedValues } from 'utils/formik'
import settingsUtils from 'utils/settings'

export const getPreparedRevenueCenterData = (
  values,
  initialValues,
  revenueCenterUuid,
  orgName,
  venueUuid
) => {
  const revenueCentersData = omit(values, [
    'menus',
    'assignedItemsToMenus',
    'menuModel',
  ])

  const updatedRevenueCenter = getChangedValues(
    revenueCentersData,
    initialValues
  )

  const revenueCenterDataToUpdate = {
    ...updatedRevenueCenter,
    uuid: revenueCenterUuid,
  }

  const revenueCenterToCreate = {
    ...updatedRevenueCenter,
    organization_name: orgName,
    venueUuid,
  }

  return {
    revenueCenterDataToUpdate,
    revenueCenterToCreate,
  }
}

export const getPreparedMenusData = (values, initialValues) => {
  const updatedMenus = getChangedValues(
    values.menus,
    initialValues.menus
  )

  const menusWithUpdatedPreOrder = updatedMenus.map((menu) => {
    const isPreOrderConfigurationModel =
      menu?.configurationModelName?.includes('Pre-Order')

    if (isPreOrderConfigurationModel) {
      return {
        ...menu,
        preorder: true,
      }
    }

    return {
      ...menu,
      preorder: false,
    }
  })

  const menusKeysToPick = [
    'name',
    'displayName',
    'configurationModelId',
    'uuid',
    'operatingHours',
    'orderAheadHours',
    'standId',
    'merchantId',
    'preorder',
  ]

  const menusDataToUpdate = menusWithUpdatedPreOrder
    .filter(({ isNewMenu }) => !isNewMenu)
    .map((menu) => pick(menu, menusKeysToPick))

  const menusDataToCreate = menusWithUpdatedPreOrder
    .filter(({ isNewMenu }) => isNewMenu)
    .map((menu) => pick(menu, menusKeysToPick))

  return {
    menusDataToUpdate,
    menusDataToCreate,
  }
}

export const getPreparedItemsData = (
  intialAssignedItemsUuids,
  assignedItemsToMenus,
  allItems
) => {
  // compare assignedItemsUuids between intialValues and changedValues
  const changedStateItemsUuids = assignedItemsToMenus?.map(
    (changedItem) => {
      const initialItem = intialAssignedItemsUuids?.find(
        (initialItem) =>
          initialItem?.menuUuid === changedItem?.menuUuid
      )

      const addedMenuUuids =
        changedItem?.assignedItemsUuids?.filter(
          (item) => !initialItem?.assignedItemsUuids?.includes(item)
        ) ?? []

      const removedMenuUuids =
        initialItem?.assignedItemsUuids?.filter(
          (item) => !changedItem?.assignedItemsUuids?.includes(item)
        ) ?? []

      return {
        menuUuid: changedItem?.menuUuid,
        assignedItemsUuids: [...addedMenuUuids, ...removedMenuUuids],
      }
    }
  )

  // transform data to get grouped items by itemUuid and assignedMenusUuid to particular itemUuid
  const assignedMenusToItem = changedStateItemsUuids.reduce(
    (acc, cur) => {
      cur.assignedItemsUuids.forEach((itemUuid) => {
        const existingItemIndex = acc.findIndex(
          (accItem) => accItem.itemUuid === itemUuid
        )

        if (existingItemIndex !== -1) {
          // if itemUuid exists, update assignedMenusUuid
          acc[existingItemIndex].assignedMenusUuid.push(cur.menuUuid)
        } else {
          // if itemUuid does not exist, add new object
          acc.push({
            itemUuid,
            assignedMenusUuid: [cur.menuUuid],
          })
        }
      })

      return acc
    },
    []
  )

  const transformedItems = assignedMenusToItem.reduce((acc, cur) => {
    const currentItem = allItems.find(
      ({ uuid }) => uuid === cur.itemUuid
    )

    // removed menuUuids
    const filteredMenuUuids = currentItem.menuUuids.filter(
      (menuUuid) => !cur.assignedMenusUuid.includes(menuUuid)
    )

    const addedMenuUuids = cur.assignedMenusUuid.filter(
      (menuUuid) => !currentItem.menuUuids.includes(menuUuid)
    )

    return [
      ...acc,
      {
        uuid: cur.itemUuid,
        menuUuids: [...filteredMenuUuids, ...addedMenuUuids].filter(
          // remove all falsy values
          (menuUuid) => !!menuUuid
        ),
      },
    ]
  }, [])

  return transformedItems
}

/**
 * Prepares settings data for submission based on the given parameters.
 *
 * @param {Object} settings - The settings object.
 * @param {Object} initialSettings - The initial settings object.
 * @param {String} venueUuid - The venue uuid.
 * @param {String} standId - The stand id.
 *
 * @returns {Array} - The settings data to send.
 *
 * @description
 * - When creating or updating tip configuration settings, all tip configuration settings need to be sent,
 *   including those that have not been changed.
 * - If `provide_tip_suggestions` is false, tip configuration settings should be filtered out from the final settings.
 *
 */

export const getPreparedSettingsData = (
  settings,
  initialSettings,
  venueUuid,
  standId
) => {
  const { provide_tip_suggestions } = settings

  const tipConfigurationSettingsNames =
    settingsUtils.getAllTipConfigurationSettingsNames()

  // generate settings to send after processing
  const processedSettings =
    settingsUtils.renderFieldsToCreateSettings(
      settings,
      initialSettings,
      venueUuid,
      {
        standId,
      }
    )

  const processedSettingNames = processedSettings.map(
    (setting) => setting.name
  )

  // prepare tip configuration settings to send
  const defaultTipConfigurationSettings =
    settingsUtils.prepareDefaultTipConfigurationsToSend(
      initialSettings,
      processedSettingNames,
      venueUuid,
      { standId }
    )

  // total settings with removed null values from tip_values
  const totalSettings = [
    ...processedSettings,
    ...defaultTipConfigurationSettings,
  ].map((setting) => {
    if (setting.name === 'tip_values') {
      return {
        ...setting,
        value: compact(setting.value),
      }
    }
    return setting
  })

  // filter out tip configuration settings if tip suggestions are not enabled
  const settingsExcludingTipConfigurations =
    processedSettings?.filter(
      (setting) =>
        !tipConfigurationSettingsNames.includes(setting.name)
    )

  return provide_tip_suggestions
    ? totalSettings
    : settingsExcludingTipConfigurations
}
